/* eslint-disable react-refresh/only-export-components */
import type { RegisteredRouter, RouteIds } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { createContext, useContext } from 'react'

import type {
  LuggageFilterValue,
  SortFilterValue,
  StopsFilterValue,
} from '@/lib/schemas/flight-results-search.schema'

import { useFilters } from '@/utils/use-search-params'

export interface FiltersStateContextValue {
  filters: FlightFilterParams
  setFilters: (partialFilters: Partial<FlightFilterParams>) => void
}

/**
 * Shared shape of the filter-related URL search params. Both the flight results
 * route and the NLP (vols-multi) route expose these params, so the filter
 * components read/write them through this context instead of binding to a
 * single route id (which would throw a TanStack Router invariant error when
 * mounted on another route).
 */
interface FlightFilterParams {
  filterAirlines?: string
  filterDurationMax?: number
  filterDurationMin?: number
  filterLuggage?: LuggageFilterValue
  filterPriceMax?: number
  filterPriceMin?: number
  filterSort?: SortFilterValue
  filterStops?: StopsFilterValue
  filterTimeRanges?: string
}

const FiltersStateContext = createContext<FiltersStateContextValue | null>(null)

interface FiltersStateProviderProps<TId extends RouteIds<RegisteredRouter['routeTree']>> {
  routeId: TId
  children: ReactNode
}

/**
 * Binds the filter components to the search params of `routeId`. The cast to the
 * loose {@link FiltersStateContextValue} is contained here so the consuming
 * filter components stay route-agnostic and fully typed.
 */
export function FiltersStateProvider<TId extends RouteIds<RegisteredRouter['routeTree']>>({
  routeId,
  children,
}: FiltersStateProviderProps<TId>) {
  const { filters, setFilters } = useFilters(routeId)

  return (
    <FiltersStateContext.Provider
      value={{ filters, setFilters } as unknown as FiltersStateContextValue}
    >
      {children}
    </FiltersStateContext.Provider>
  )
}

export function useFiltersState(): FiltersStateContextValue {
  const context = useContext(FiltersStateContext)
  if (context === null) {
    throw new Error('useFiltersState must be used within a FiltersStateProvider')
  }
  return context
}
